import React from "react";
import { Link } from "react-router-dom";
import { Mail, MapPin, Phone, ArrowLeft } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const CONTACT = {
  email: "info@greek-export.gr",
  addresses: [
    { line1: "Καρτερού 58", line2: "Ηράκλειο Κρήτης", map: "Καρτερού 58 Ηράκλειο Κρήτης" },
    { company: "Tetrabyte PC IKE", line1: "Ειρήνης 7", line2: "Πολυδένδρι Αττικής", map: "Ειρήνης 7 Πολυδένδρι Αττικής" },
  ],
  phone: "+30 6989164257",
  phone_display: "6989 164 257",
};

export default function Contact() {
  const { t, lang } = useI18n();
  const L = lang === "el";

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-900" data-testid="contact-page">
      {/* Header — standard across public pages */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/85 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="contact-brand">
            <div className="w-9 h-9 rounded-md bg-[#0052CC] flex items-center justify-center text-white font-bold">GE</div>
            <span className="font-heading font-bold text-slate-900">{t("app.name")}</span>
          </Link>
          <div className="hidden md:flex items-center gap-6 text-sm font-medium text-slate-700">
            <Link to="/hire-rep" className="hover:text-slate-900" data-testid="contact-nav-hire-rep">{L ? "Πρόσληψη Πωλητή Εξωτερικού" : "Hire Sales Rep"}</Link>
            <Link to="/directories" className="hover:text-slate-900" data-testid="contact-nav-directories">{L ? "Επαγγελματικοί Κατάλογοι" : "Business Directories"}</Link>
            <Link to="/contact" className="text-[#0052CC] font-semibold" data-testid="contact-nav-contact">{L ? "Επικοινωνία" : "Contact"}</Link>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Link to="/login" data-testid="contact-nav-login" className="px-4 py-2 text-sm font-semibold bg-[#0052CC] text-white rounded-md hover:bg-[#0043A8] transition-colors">
              {t("auth.login")}
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#2563EB] mb-3">
            {L ? "Επικοινωνία" : "Contact"}
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-tight">
            {L ? "Ας μιλήσουμε." : "Let's talk."}
          </h1>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed">
            {L
              ? "Επικοινωνήστε μαζί μας για ερωτήσεις, συνεργασίες, ή να ξεκινήσετε την πορεία σας στις διεθνείς αγορές."
              : "Get in touch for questions, partnerships, or to start your journey into international markets."}
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-5">
          <a
            href={`mailto:${CONTACT.email}`}
            className="group bg-white border border-slate-200 rounded-2xl p-6 hover:border-[#0052CC] hover:shadow-sm transition-all"
            data-testid="contact-email-card"
          >
            <div className="w-11 h-11 rounded-full bg-[#0052CC]/10 text-[#0052CC] flex items-center justify-center mb-4 group-hover:bg-[#0052CC] group-hover:text-white transition-colors">
              <Mail className="w-5 h-5" />
            </div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Email</div>
            <div className="mt-1 font-heading text-lg font-bold text-slate-900 break-all">{CONTACT.email}</div>
            <div className="mt-3 text-xs text-slate-500">
              {L ? "Απαντάμε συνήθως εντός 24 ωρών" : "We usually reply within 24 hours"}
            </div>
          </a>

          <a
            href={`tel:${CONTACT.phone}`}
            className="group bg-white border border-slate-200 rounded-2xl p-6 hover:border-emerald-600 hover:shadow-sm transition-all"
            data-testid="contact-phone-card"
          >
            <div className="w-11 h-11 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mb-4 group-hover:bg-emerald-600 group-hover:text-white transition-colors">
              <Phone className="w-5 h-5" />
            </div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">{L ? "Τηλέφωνο" : "Phone"}</div>
            <div className="mt-1 font-heading text-lg font-bold text-slate-900">{CONTACT.phone_display}</div>
            <div className="mt-3 text-xs text-slate-500">
              {L ? "Δευτέρα–Παρασκευή, 9:00–18:00 EET" : "Mon–Fri, 09:00–18:00 EET"}
            </div>
          </a>

          <div className="group bg-white border border-slate-200 rounded-2xl p-6">
            <div className="w-11 h-11 rounded-full bg-orange-100 text-orange-700 flex items-center justify-center mb-4">
              <MapPin className="w-5 h-5" />
            </div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">{L ? "Διευθύνσεις" : "Addresses"}</div>
            {CONTACT.addresses.map((a, i) => (
              <div key={a.map} className={i ? "mt-4 pt-4 border-t border-slate-100" : ""} data-testid={`contact-address-${i + 1}`}>
                {a.company && <div className="mt-1 text-xs font-semibold text-slate-500">{a.company}</div>}
                <div className="mt-1 font-heading text-lg font-bold text-slate-900 leading-tight">
                  {a.line1}<br />
                  {a.line2}
                </div>
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(a.map)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`contact-map-link-${i + 1}`}
                  className="mt-2 inline-flex items-center gap-1 text-xs text-orange-700 font-semibold hover:underline"
                >
                  {L ? "Άνοιγμα στους χάρτες →" : "Open in maps →"}
                </a>
              </div>
            ))}
          </div>
        </div>

        {/* CTA to the client onboarding wizard */}
        <div className="mt-20 bg-slate-900 text-white rounded-2xl overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-2">
            <div className="p-8 lg:p-12">
              <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-slate-400">
                {L ? "Ή ξεκινήστε αμέσως" : "Or get started now"}
              </div>
              <h2 className="mt-2 font-heading text-2xl lg:text-3xl font-bold leading-tight">
                {L ? "Νοικιάστε τον ιδανικό εκπρόσωπο σε 6 βήματα" : "Rent the ideal sales rep in 6 steps"}
              </h2>
              <p className="mt-4 text-slate-300 text-sm leading-relaxed">
                {L
                  ? "Πείτε μας τι εξάγετε, σε ποιες αγορές στοχεύετε και θα σας βρούμε επαγγελματίες πωλητές."
                  : "Tell us what you export and your target markets — we'll match you with pro sales reps."}
              </p>
              <Link
                to="/#join"
                className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-white text-slate-900 font-semibold rounded-md hover:bg-slate-100"
                data-testid="contact-wizard-cta"
              >
                {L ? "Ξεκινήστε τώρα" : "Start now"}
              </Link>
            </div>
            <div className="hidden md:block bg-gradient-to-br from-[#0F1E3D] via-[#0052CC] to-[#2563EB] relative overflow-hidden">
              <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-emerald-500/30 blur-3xl" />
              <div className="absolute bottom-8 -left-8 w-56 h-56 rounded-full bg-white/10 blur-2xl" />
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 py-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex flex-col sm:flex-row justify-between gap-4 text-sm text-slate-500">
          <div>© {new Date().getFullYear()} Ελληνικό Δίκτυο Εξαγωγών</div>
          <div className="flex gap-6">
            <Link to="/" className="hover:text-slate-900">{L ? "Αρχική" : "Home"}</Link>
            <Link to="/login" className="hover:text-slate-900">{L ? "Σύνδεση" : "Login"}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
